import React, { useEffect, useState, useRef, useCallback } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import CustomerSideBar from "./CustomerSideBar"; 
import { API_URL } from "../../constant";

const CustomerLayout = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [containerStyle, setContainerStyle] = useState({});
  const [showAgentFormModal, setShowAgentFormModal] = useState(false);
  const [inactivityTimeout, setInactivityTimeout] = useState(15*60000);
  
  // Refs to track timers
  const logoutTimerRef = useRef(null);
  const eventsSetupRef = useRef(false);
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    fetchUserData();
    
    // Cleanup on unmount
    return () => {
      clearTimeout(logoutTimerRef.current);
      removeEventListeners();
    };
  }, []);

  // Fetch timeout configuration from Laravel backend
  const fetchTimeoutConfig = useCallback(async () => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) return;

      // Fetch timeout configuration from Laravel API
      const configResponse = await fetch(`${API_URL}/api/config/inactivity-timeout`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
      });

      if (configResponse.ok) {
        const configData = await configResponse.json();
        const timeoutMinutes = configData.timeout_minutes || 1;
        setInactivityTimeout(timeoutMinutes * 60000); // Convert minutes to milliseconds
        
        // Store in localStorage for quick access
        localStorage.setItem('inactivity_timeout', timeoutMinutes);
      }
    } catch (error) {
      console.error("Error fetching timeout config:", error);
      // Use localStorage fallback if API call fails
      const storedTimeout = localStorage.getItem('inactivity_timeout');
      if (storedTimeout) {
        setInactivityTimeout(parseInt(storedTimeout) * 60000);
      }
    }
  }, []);

  // Function to reset the logout timer
  const resetLogoutTimer = useCallback(() => {
    clearTimeout(logoutTimerRef.current);
    lastActivityRef.current = Date.now();
    
    logoutTimerRef.current = setTimeout(() => {
      handleLogout();
    }, inactivityTimeout);
  }, [inactivityTimeout]);

  // Function to handle user logout
  const handleLogout = useCallback(() => {
    clearTimeout(logoutTimerRef.current);
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user");
    localStorage.removeItem("inactivity_timeout");
    navigate("/sign_in");
  }, [navigate]);

  // Function to track user activity
  const trackActivity = useCallback(() => {
    // Only reset timer if user is active for more than 1 second since last activity
    const now = Date.now();
    if (now - lastActivityRef.current > 1000) {
      resetLogoutTimer();
    }
  }, [resetLogoutTimer]);

  // Function to setup event listeners
  const setupEventListeners = useCallback(() => {
    if (eventsSetupRef.current) return;
    
    // Listen for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, trackActivity);
    });
    
    // Also listen for visibility change (tab switching)
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    eventsSetupRef.current = true;
  }, [trackActivity]);

  // Handle visibility change (tab switching)
  const handleVisibilityChange = useCallback(() => {
    if (!document.hidden) {
      // User switched back to tab, reset timer
      resetLogoutTimer();
    }
  }, [resetLogoutTimer]);

  // Function to remove event listeners
  const removeEventListeners = useCallback(() => {
    if (!eventsSetupRef.current) return;
    
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.removeEventListener(event, trackActivity);
    });
    
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    
    eventsSetupRef.current = false;
  }, [trackActivity, handleVisibilityChange]);

  const enforceParentalConsentGate = useCallback(async (token) => {
    const requiresParentalConsent =
      localStorage.getItem("requires_parental_consent") === "true";

    if (!requiresParentalConsent) {
      return true;
    }

    try {
      const consentResponse = await fetch(`${API_URL}/api/parents-consent`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (consentResponse.ok) {
        localStorage.removeItem("requires_parental_consent");
        return true;
      }

      if (consentResponse.status === 404) {
        navigate("/parents-consent", { replace: true, state: { gated: true } });
        return false;
      }

      if (consentResponse.status === 401) {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("user");
        navigate("/sign_in", { replace: true });
        return false;
      }
    } catch (consentError) {
      console.error("Error checking parental consent:", consentError);
    }

    return true;
  }, [navigate]);

  const fetchUserData = async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      navigate("/sign_in");
      return;
    }

    setIsLoading(true);
    try {
      // Get user data
      const userResponse = await fetch(`${API_URL}/api/user`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
      });

      if (!userResponse.ok) {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("user");
        localStorage.removeItem("inactivity_timeout");
        throw new Error("Invalid or expired token");
      }

      const canContinue = await enforceParentalConsentGate(token);
      if (!canContinue) {
        setIsLoading(false);
        return;
      }
      
      const mainUser = await userResponse.json();
      
      // Get customer profile
      const customerResponse = await fetch(`${API_URL}/api/customer/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
      });
      
      let customerData = null;
      if (customerResponse.ok) {
        customerData = await customerResponse.json();
      }
      
      // Check if user is advertising agent
      let isAdvertisingAgent = false;
      let agentRole = "Customer";
      let agentType = null;
      
      try {
        const referralResponse = await fetch(`${API_URL}/api/customer/referral/info`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
        });
        
        if (referralResponse.ok) {
          const referralData = await referralResponse.json();
          isAdvertisingAgent = referralData.is_advertising_agent || false;
          
          if (isAdvertisingAgent) {
            // Get agent details
            try {
              const agentResponse = await fetch(`${API_URL}/api/customer/me`, {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
                },
              });
              
              if (agentResponse.ok) {
                const agentData = await agentResponse.json();
                agentType = agentData?.user?.role; 
                agentRole = agentType;
              }
            } catch (error) {
              console.error("Error fetching agent details:", error);
            }
          }
        }
      } catch (error) {
        console.error("Error checking agent status:", error);
      }

      // Transform data for CustomerSideBar
      const transformedUser = {
        firstName: customerData?.user?.first_name || mainUser.user?.name?.split(' ')[0] || "Customer",
        sureName: customerData?.user?.last_name || mainUser.user?.name?.split(' ').slice(1).join(' ') || "",
        fullName: customerData?.user?.first_name ? 
          `${customerData.user.first_name} ${customerData.user.last_name}`.trim() : 
          mainUser.user?.name || "Customer",
        role: agentRole,
        avatar: customerData?.user?.photo_url || mainUser.user?.avatar || "",
        email: customerData?.user?.email || mainUser.user?.email,
        id: customerData?.user?.customer_id || mainUser.user?.id,
        isAdvertisingAgent: isAdvertisingAgent,
        agentType: agentType,
        referralCode: customerData?.user?.referral_code || "",
      };

      setUserData(transformedUser);
      
      // Set background based on role
      setContainerStyle(getContainerStyle(agentRole));
      
      // Fetch timeout configuration and setup activity tracking
      await fetchTimeoutConfig();
      
    } catch (error) {
      console.error("Error fetching user data:", error);
      setError(error.message);
      navigate("/sign_in");
    } finally {
      setIsLoading(false);
    }
  };

  // Setup activity tracking when userData is available
  useEffect(() => {
    if (userData && !eventsSetupRef.current) {
      resetLogoutTimer();
      setupEventListeners();
    }
    
    return () => {
      clearTimeout(logoutTimerRef.current);
    };
  }, [userData, resetLogoutTimer, setupEventListeners]);

  // Reset timer when inactivityTimeout changes
  useEffect(() => {
    if (userData) {
      resetLogoutTimer();
    }
  }, [inactivityTimeout, userData, resetLogoutTimer]);

  const getContainerStyle = (role) => {
    let background = '';
    
    switch(role) {
      case 'Senior Customer Advertising Agent':
        background = 'linear-gradient(to bottom, #0d6efd, white)';
        break;
      case 'Mini Customer Advertising Agent':
        background = 'linear-gradient(to bottom, #0000a0, white)';
        break;
      case 'Customer':
      default:
        background = 'linear-gradient(to bottom, #6f42c1, white)';
        break;
    }
    
    return {
      display: "flex",
      flexDirection: "column",
      marginTop: "0px",
      marginBottom: "0px",
      background: background,
      width: "100%",
      minHeight: "100vh",
      fontFamily: "'Poppins', sans-serif"
    };
  };

  if (isLoading) {
    return (
      <div
        style={{
          ...containerStyle,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div style={{ marginTop: '20px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          ...containerStyle,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div style={{ color: "red" }}>Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="pagecontainer1" style={containerStyle}>
      <CustomerSideBar 
        user={userData} 
        onAgentRequest={() => setShowAgentFormModal(true)} 
      />
      <Outlet context={{ 
        userData, 
        fetchUserData,
        showAgentFormModal,
        setShowAgentFormModal,
        handleLogout 
      }} />
    </div>
  );
};

export default CustomerLayout;